import React from "react";
import { OrderStatusKey } from "../../../definitions/orderStatus";
import { Tag } from "antd";
import Text from "../../../components/Text/Text";
import { StepsStyled } from "../../../components/StepAntd/StepAntd";
import StatusBadge from "../../../components/Badge/StatusBadge";
import color from "../../../resource/color";
import moment from "moment";

const { Step } = StepsStyled;

export const StatusModalDescription = ({
  status,
  approvers,
}: {
  status: OrderStatusKey;
  approvers: any;
}) => {
  const title = {
    WAIT_APPROVE_ORDER: "การอนุมัติคำสั่งซื้อพิเศษ (Special Request)",
    WAIT_APPROVE_OVER_CREDIT: "การอนุมัติวงเงินเกิน (Over Credit)",
    WAIT_APPROVE_SALE_ORDER: "การอนุมัติใบสั่งขาย (Order Approve)",
  };
  const isOneStep = status === "WAIT_APPROVE_ORDER" || status === "WAIT_APPROVE_SALE_ORDER";
  const hasData = approvers && approvers.length > 0;

  const sequenceMap: { [key: number]: any[] } = {};
  if (!isOneStep) {
    approvers.forEach((approver: any) => {
      if (!sequenceMap[approver.sequence]) {
        sequenceMap[approver.sequence] = [];
      }
      sequenceMap[approver.sequence].push(approver);
    });
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <Text level={5} fontWeight={700}>
        {title[status]}
      </Text>
      <br />
      <br />
      {hasData ? (
        isOneStep ? (
          <StepsStyled direction='vertical' current={99}>
            <Step
              title={
                <>
                  <b>ผู้อนุมัติ:&nbsp;</b>
                  {approvers[0]?.role}&nbsp;จำนวน&nbsp;{approvers[0]?.sequenceRequired}
                  &nbsp;คน
                </>
              }
              description={
                <div style={{ marginTop: 8 }}>
                  <ApproverItems
                    approvers={approvers}
                    requireAmount={approvers[0]?.sequenceRequired}
                  />
                </div>
              }
            />
          </StepsStyled>
        ) : (
          <StepsStyled direction='vertical' current={99}>
            {approvers &&
              sequenceMap &&
              Object.keys(sequenceMap).map((seq: any) => {
                const role = seq[0]?.role;
                return (
                  <Step
                    key={seq}
                    title={
                      <>
                        <b>ผู้อนุมัติลำดับที่ {seq}:&nbsp;</b>
                        {role}&nbsp;จำนวน&nbsp;{sequenceMap[parseInt(seq)][0]?.sequenceRequired}
                        &nbsp;คน
                      </>
                    }
                    description={
                      <div style={{ marginTop: 8 }}>
                        <ApproverItems
                          approvers={sequenceMap[parseInt(seq)]}
                          requireAmount={sequenceMap[parseInt(seq)][0]?.sequenceRequired}
                        />
                      </div>
                    }
                  />
                );
              })}
          </StepsStyled>
        )
      ) : (
        <Text
          color='background4'
          fontFamily='Sarabun'
          style={{ textAlign: "center", display: "block", margin: 12 }}
        >
          ไม่พบข้อมูล
        </Text>
      )}
    </div>
  );
};

const ApprovalRow = ({
  no,
  name,
  role,
  isApproved = false,
  isRejected = false,
  time,
}: {
  no: number;
  name: string | undefined;
  role: string | undefined;
  isApproved: boolean;
  isRejected: boolean;
  time?: string;
}) => {
  const badgeLabel =
    isRejected && isApproved
      ? "ไม่อนุมัติ (อัตโนมัติ)"
      : isRejected
        ? "ไม่อนุมัติ"
        : isApproved
          ? "อนุมัติแล้ว"
          : "รออนุมัติ";
  const badgeStatus = isRejected ? "error" : isApproved ? "success" : "warning";
  const hasAction = isApproved || isRejected;

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <span>{`คนที่ ${no} ${name ? name : role}`}</span>
        <StatusBadge label={badgeLabel} bgColor={color[badgeStatus]} />
      </div>
      {hasAction && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: -6,
            marginBottom: 8,
          }}
        >
          <span>{role}</span>
          <span>{time}</span>
        </div>
      )}
    </>
  );
};

const ApproverItems = ({
  approvers,
  requireAmount,
}: {
  approvers: any[];
  requireAmount: number;
}) => {
  const approved = approvers.filter((a) => ["APPROVE", "APPROVED"].includes(a.action));
  const hasRejected = approvers.find((a) => ["REJECT", "REJECTED"].includes(a.action));

  return (
    <div style={{ marginTop: 8 }}>
      {Array.from({ length: requireAmount }, (_, i) => {
        const approver = hasRejected ? approved[i] || hasRejected : approved[i];
        return approver ? (
          <ApprovalRow
            key={i}
            no={i + 1}
            name={approver.userStaffName}
            role={approver.role}
            isApproved={approver.action === "APPROVED" || approver.action === "APPROVE"}
            isRejected={!!hasRejected}
            time={approver.updateAt ? moment(approver.updateAt).format("DD/MM/YYYY, HH:mm") : ""}
          />
        ) : (
          <ApprovalRow
            key={i}
            no={i + 1}
            name=''
            role={approvers[0]?.role}
            isApproved={false}
            isRejected={false}
          />
        );
      })}
    </div>
  );
};

/* 
ตัวอย่างข้อมูล approvers

"orderCreditApprovers": [
                {
                    "orderCreditApproverId": "a093e176-1661-48f2-9f8c-465a183f84b7",
                    "orderId": "12a86b62-c0e2-4b91-96d4-6a368d199d44",
                    "userStaffId": "5136e447-501d-480b-9556-9c781c51b6b5",
                    "userStaffName": "ภานุวัตร เดิมชัยภูมิ",
                    "role": null,
                    "sequence": 1,
                    "sequenceRequired": 1,
                    "action": "PENDING",
                    "approveType": "CREDIT",
                    "reason": null,
                    "updateAt": "2025-12-18T09:24:22.389Z",
                    "updateBy": "system",
                    "isDeleted": false
                },
                {
                    "orderCreditApproverId": "d06d92ec-8499-415d-96d7-06bc3de819ab",
                    "orderId": "12a86b62-c0e2-4b91-96d4-6a368d199d44",
                    "userStaffId": "fce56fad-6947-4d29-88a0-6acc15a62391",
                    "userStaffName": "Aom Aom",
                    "role": null,
                    "sequence": 1,
                    "sequenceRequired": 1,
                    "action": "PENDING",
                    "approveType": "CREDIT",
                    "reason": null,
                    "updateAt": "2025-12-18T09:24:22.389Z",
                    "updateBy": "system",
                    "isDeleted": false
                }
            ],
            "orderSoApprovers": [],
*/
